import { useEffect, useState } from 'react';
import { Progress } from '../../components/ui/progress';
import { Music } from 'lucide-react';

export const API = process.env.REACT_APP_BACKEND_URL;

export const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'ar', label: 'Arabic' }, { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' }, { code: 'de', label: 'German' }, { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' }, { code: 'ko', label: 'Korean' }, { code: 'pt', label: 'Portuguese' },
  { code: 'hi', label: 'Hindi' }, { code: 'ru', label: 'Russian' }, { code: 'it', label: 'Italian' },
];

export function GenerationProgress({ active, progress, message, etaLabel }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) { setElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(id);
  }, [active]);
  if (!active) return null;
  const mm = String(Math.floor(elapsed / 60)).padStart(1, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  const hasRealPct = typeof progress === 'number' && progress > 0;
  return (
    <div className="fixed inset-x-0 bottom-0 top-auto sm:absolute sm:inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center z-30 sm:z-10 rounded-t-2xl sm:rounded-lg shadow-2xl sm:shadow-none p-6 sm:p-0" data-testid="generation-progress">
      <div className="w-16 h-16 mb-4 relative">
        <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
        <div className="absolute inset-0 rounded-full border-4 border-t-emerald-500 animate-spin" />
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-emerald-600" data-testid="generation-progress-value">
          {hasRealPct ? `${progress}%` : `${mm}:${ss}`}
        </span>
      </div>
      {hasRealPct
        ? <Progress value={progress} className="w-48 h-2 mb-2" />
        : <div className="w-48 h-2 mb-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full w-1/3 bg-emerald-500 rounded-full animate-[loading_1.4s_ease-in-out_infinite]" style={{ animation: 'gp-slide 1.4s ease-in-out infinite' }} /></div>
      }
      <p className="text-sm font-medium text-text-primary" data-testid="generation-progress-message">{message || 'Generating...'}</p>
      <p className="text-[10px] text-gray-400 mt-1" data-testid="generation-progress-elapsed">
        {hasRealPct ? 'This may take a moment' : `${mm}:${ss} elapsed · ${etaLabel || 'typically 60–90s'}`}
      </p>
      <style>{`@keyframes gp-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
    </div>
  );
}

export async function downloadFile(url, filename) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch { window.open(url, '_blank'); }
}

export function PreviewContent({ item }) {
  if (item.type === 'image' && item.url) return <img src={item.url} alt="" className="w-full rounded-lg" />;
  if (item.type === 'video' && item.url) return <video src={item.url} controls className="w-full rounded-lg" />;
  if (item.type === 'music') {
    const src = item.url || (item.audio_base64 ? `data:audio/mp3;base64,${item.audio_base64}` : null);
    return <div className="text-center py-6"><Music className="h-16 w-16 mx-auto mb-4 text-pink-400" />{src && <audio controls src={src} className="w-full" />}</div>;
  }
  return <div className="max-h-[400px] overflow-y-auto"><div className="prose prose-sm text-sm whitespace-pre-wrap">{item.content || item.prompt}</div></div>;
}
